import {
  Business,
  Email,
  Phone,
  LocationCity,
  Home,
  Info,
  CalendarToday,
  ArrowBack,
  Edit,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Typography,
} from "@mui/material";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { fetchOrganizationById } from "../../redux/organization/organizationAction.jsx";

const ViewOrganization = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const orgData = useSelector(
    (state) => state.organization?.currentOrganization?.data?.data
  );
  const loading = useSelector(
    (state) => state.organization?.currentOrganization?.loading
  );

  useEffect(() => {
    if (id) {
      dispatch(fetchOrganizationById(id));
    }
  }, [id, dispatch]);

  const handleBack = () => {
    navigate("/organization/list");
  };

  const getStatusColor = (status) => {
    return status === 1 ? "success" : "error";
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", my: 8 }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (!orgData) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" color="error">
          Organization not found.
        </Typography>
        <Button
          variant="contained"
          onClick={handleBack}
          startIcon={<ArrowBack />}
          sx={{ mt: 2 }}
        >
          Back to List
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: "100vw", mx: "auto", p: 3 }}>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={12} md={8} lg={9}>
          <Typography variant="h4" fontWeight="bold">
            Organization Details
          </Typography>
        </Grid>
        <Grid
          item
          xs={12}
          sm={12}
          md={4}
          lg={3}
          sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}
        >
          <Button
            variant="contained"
            onClick={() => navigate(`/organization/edit/${id}`)}
            startIcon={<Edit />}
            sx={{
              backgroundColor: "#000",
              color: "#fff",
              "&:hover": { backgroundColor: "#333" },
            }}
          >
            Edit
          </Button>
          <Button
            variant="outlined"
            color="error"
            onClick={handleBack}
            startIcon={<ArrowBack />}
          >
            Back
          </Button>
        </Grid>
      </Grid>

      <Card elevation={3} sx={{ borderRadius: 2 }}>
        <CardContent sx={{ p: 4 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 3,
            }}
          >
            <Typography
              variant="h5"
              sx={{ display: "flex", alignItems: "center", fontWeight: "bold" }}
            >
              <Business sx={{ mr: 1, color: "#000" }} />
              Basic Information
            </Typography>
            <Chip
              label={orgData.status === 1 ? "Active" : "Inactive"}
              color={getStatusColor(orgData.status)}
              icon={<Info />}
              variant="filled"
              sx={{ fontWeight: "bold" }}
            />
          </Box>

          <Divider sx={{ mb: 4 }} />

          <Grid container spacing={4}>
            <Grid item xs={12} sm={6}>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ display: "flex", alignItems: "center", mb: 0.5 }}
              >
                <Business sx={{ fontSize: "small", mr: 1 }} />
                Organization Name
              </Typography>
              <Typography variant="h6" fontWeight="500">
                {orgData.org_name}
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ display: "flex", alignItems: "center", mb: 0.5 }}
              >
                <Home sx={{ fontSize: "small", mr: 1 }} />
                Hospital Name
              </Typography>
              <Typography variant="h6" fontWeight="500">
                {orgData.hospital_name}
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ display: "flex", alignItems: "center", mb: 0.5 }}
              >
                <Email sx={{ fontSize: "small", mr: 1 }} />
                Email Address
              </Typography>
              <Typography variant="h6" fontWeight="500">
                {orgData.email}
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ display: "flex", alignItems: "center", mb: 0.5 }}
              >
                <Phone sx={{ fontSize: "small", mr: 1 }} />
                Phone Number
              </Typography>
              <Typography variant="h6" fontWeight="500">
                {orgData.phone}
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ display: "flex", alignItems: "center", mb: 0.5 }}
              >
                <LocationCity sx={{ fontSize: "small", mr: 1 }} />
                City
              </Typography>
              <Typography variant="h6" fontWeight="500">
                {orgData.city}
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ display: "flex", alignItems: "center", mb: 0.5 }}
              >
                <CalendarToday sx={{ fontSize: "small", mr: 1 }} />
                Created Date
              </Typography>
              <Typography variant="h6" fontWeight="500">
                {orgData.createdAt
                  ? new Date(orgData.createdAt).toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    })
                  : "N/A"}
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ display: "flex", alignItems: "center", mb: 0.5 }}
              >
                <Home sx={{ fontSize: "small", mr: 1 }} />
                Address
              </Typography>
              <Typography variant="h6" fontWeight="500">
                {orgData.address}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ViewOrganization;
